import {Component, EventEmitter, Input, Output} from '@angular/core';
import {Company} from '../../../models/company';
import {User} from '../../../models/user';
import {getCompanyStatusMeta} from '../shared/company-status';

interface DebugRow {
    key: string;
    label: string;
    value: string;
}

@Component({
    selector: 'app-companies-settings-debug',
    templateUrl: './companies-settings-debug.component.html',
    styles: [':host { display: block; }']
})
export class CompaniesSettingsDebugComponent {
    @Input() company: Company = new Company();
    @Input() companyRaw: Record<string, unknown> | null = null;
    @Input() agents: User[] = [];
    @Input() teamUsers: User[] = [];

    @Output() copyValue = new EventEmitter<string>();

    expanded = false;

    get agentLabel(): string {
        const agent = this.agents.find((item) => item.id === this.company.agentId);
        if (!agent) {
            return this.company.agentId || '—';
        }
        return `${agent.firstName ?? ''} ${agent.lastName ?? ''}`.trim() + (agent.email ? ` (${agent.email})` : '');
    }

    get otpValue(): string {
        const manager = this.teamUsers.find((user) => {
            const role = (user.userGroupId ?? '').trim().toLowerCase();
            if (role === 'companymanager') {
                return true;
            }
            const email = (user.email ?? '').trim().toLowerCase();
            const legalEmail = (this.company.legalEmail ?? '').trim().toLowerCase();
            return !!email && !!legalEmail && email === legalEmail;
        });
        const fromManager = manager?.otp || manager?.recoveryNumber;
        const fromCompany = this.company?.otp;
        const fromRawUsers = Array.isArray(this.companyRaw?.['users'])
            ? (this.companyRaw?.['users'] as Array<{recoveryNumber?: string; otp?: string}>)
                .map((user) => user?.recoveryNumber || user?.otp)
                .find((value) => String(value || '').trim())
            : '';
        const fromRaw = this.companyRaw?.['otp']
            ?? this.companyRaw?.['OTP']
            ?? this.companyRaw?.['otpCode']
            ?? fromRawUsers;
        const value = String(fromManager || fromCompany || fromRaw || '').trim();
        return value || '—';
    }

    get rows(): DebugRow[] {
        const service = this.company.service;
        return [
            {key: 'id', label: 'Company ID', value: this.company.id || '—'},
            {key: 'otp', label: 'OTP', value: this.otpValue},
            {key: 'status', label: 'Status (raw)', value: this.company.status || '—'},
            {key: 'statusLabel', label: 'Status (label)', value: getCompanyStatusMeta(this.company.status).label},
            {key: 'createdAt', label: 'Created at', value: this.formatDate(this.company.createdAt)},
            {key: 'domain', label: 'Domain', value: this.company.domain || '—'},
            {key: 'shopDomain', label: 'Shop domain', value: this.company.shopDomain || '—'},
            {key: 'agentId', label: 'Agent', value: this.agentLabel},
            {key: 'billedPeriod', label: 'Billed period', value: this.company.billedPeriod || '—'},
            {key: 'assignedCode', label: 'Assigned code', value: this.company.assignedCode || '—'},
            {key: 'federation', label: 'Federation', value: this.company.federation || '—'},
            {key: 'serviceId', label: 'Service ID', value: this.company.service?.id || '—'},
            {key: 'serviceName', label: 'Service name', value: service?.name || '—'},
            {key: 'serviceStatus', label: 'Service status', value: service?.status || '—'},
            {key: 'stripeCustomerId', label: 'Stripe customer ID', value: this.company.stripeCustomerId || String(this.companyRaw?.['stripeCustomerId'] || '—')},
            {key: 'stripeSubscriptionId', label: 'Stripe subscription ID', value: this.company.stripeSubscriptionId || String(this.companyRaw?.['stripeSubscriptionId'] || '—')},
            {key: 'stripeSubscriptionStatus', label: 'Stripe subscription status', value: this.company.stripeSubscriptionStatus || String(this.companyRaw?.['stripeSubscriptionStatus'] || '—')},
            {key: 'nexiShopId', label: 'Nexi shop ID', value: this.company.nexiShopId || '—'},
            {key: 'nexiRatesShopId', label: 'Nexi rates shop ID', value: this.company.nexiRatesShopId || '—'},
            {key: 'appPagoShopId', label: 'AppPago shop ID', value: this.company.appPagoShopId || '—'},
            {key: 'posNexiBusinessId', label: 'POS Nexi business ID', value: this.company.posNexiBusinessId || '—'},
            {key: 'receivingEmail', label: 'Receiving email', value: this.company.receivingEmail || '—'},
            {key: 'sendingEmail', label: 'Sending email', value: this.company.sendingEmail || '—'},
        ];
    }

    get extraRawJson(): string {
        if (!this.companyRaw) {
            return '';
        }
        try {
            return JSON.stringify(this.companyRaw, null, 2);
        } catch {
            return String(this.companyRaw);
        }
    }

    copy(value: string): void {
        if (value && value !== '—') {
            this.copyValue.emit(value);
        }
    }

    private formatDate(value?: string): string {
        if (!value) {
            return '—';
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }
        return date.toLocaleString('it-IT');
    }
}
